/**
 * @module components/toolbar/TopBar
 * @summary The two-row top toolbar: tools, edit, view, zoom, export + the
 *   alignment / grouping row, plus the document name and zoom readout.
 * @description Buttons call the engine's imperative API; active states are read
 *   from the Zustand UI store (kept in sync by useEngineSync).
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 410-479.
 */
import { useEffect, useRef, useState } from 'react';
import { engine } from '../../engine/CanvasEngine';
import { useUiStore } from '../../state/uiStore';
import type { Tool } from '../../types';

/** Which surface the editor is showing. */
export type EditorView = '2d' | '3d' | 'solid';

interface Props {
  onHelp: () => void;
  /** Open the PDF sheet-set dialog. */
  onExportPdf: () => void;
  /** Which surface the editor is showing. */
  view?: EditorView;
  /** Switch between the 2D drawing and the 3D plant view. */
  onToggleView?: () => void;
  /** Switch between the 2D drawing and the part modeller. */
  onToggleSolid?: () => void;
}

export function TopBar({ onHelp, onExportPdf, view = '2d', onToggleView, onToggleSolid }: Props) {
  const { tool, zoomPct, snapOn, gridOn, curved, canUndo, canRedo } = useUiStore();
  const [docName, setDocName] = useState(engine.getDocMeta().docName);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const loadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Re-sync the name box when the document is replaced (Load / New / restore).
    const offDoc = engine.on('doc', () => setDocName(engine.getDocMeta().docName));
    const offSaved = engine.on('saved', () =>
      setSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    );
    return () => {
      offDoc();
      offSaved();
    };
  }, []);

  const setTool = (t: Tool) => engine.setTool(t);

  return (
    <div id="topbar">
      <div id="logo" title="Jeddah Cables Company">
        <img className="logo-img" src="/logo.png" alt="Jeddah Cables Company" />
      </div>

      <div id="toolbar-wrap">
        {/* ── Main toolbar row ── */}
        <div id="toolbar">
          {/* Tools */}
          <button className={'tb-btn' + (tool === 'select' ? ' active' : '')} title="Select (V)" onClick={() => setTool('select')}>▶</button>
          <button className={'tb-btn' + (tool === 'connect' ? ' active' : '')} title="Connect (C)" onClick={() => setTool('connect')}>⬡</button>
          <button className={'tb-btn' + (tool === 'pan' ? ' active' : '')} title="Pan (H)" onClick={() => setTool('pan')}>✥</button>
          <button className={'tb-btn' + (curved ? ' active' : '')} title="Toggle curved connectors" onClick={() => engine.toggleCurve()}>⌒</button>
          <span className="tb-sep" />
          {/* Edit */}
          <button className="tb-btn" title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={() => engine.undo()}>↩</button>
          <button className="tb-btn" title="Redo (Ctrl+Y)" disabled={!canRedo} onClick={() => engine.redo()}>↪</button>
          <span className="tb-sep" />
          {/* Clipboard */}
          <button className="tb-btn" title="Copy (Ctrl+C)" onClick={() => engine.copy()}>⎘</button>
          <button className="tb-btn" title="Paste (Ctrl+V)" onClick={() => engine.paste()}>⎗</button>
          <button className="tb-btn" title="Delete (Del)" style={{ color: '#ffb3b3' }} onClick={() => engine.delete()}>✕</button>
          <span className="tb-sep" />
          {/* View */}
          <button className={'tb-btn' + (snapOn ? ' active' : '')} title="Snap to grid" onClick={() => engine.toggleSnap()}>⊞</button>
          <button className={'tb-btn' + (gridOn ? ' active' : '')} title="Show grid" onClick={() => engine.toggleGrid()}>⠿</button>
          <span className="tb-sep" />
          {/* Zoom */}
          <button className="tb-btn" title="Zoom out" onClick={() => engine.zoomOut()}>−</button>
          <span id="zoom-display" title="Reset zoom" onClick={() => engine.zoomReset()}>{zoomPct}%</span>
          <button className="tb-btn" title="Zoom in" onClick={() => engine.zoomIn()}>+</button>
          <button className="tb-btn" title="Fit to screen" onClick={() => engine.fit()}>⊡ Fit</button>
          <span className="tb-sep" />
          {/* Export */}
          <button className="tb-btn" title="New document" onClick={() => engine.newDocument()}>✚ New</button>
          <button className="tb-btn" title="Export PNG" onClick={() => engine.exportPng()}>🖼</button>
          {/* One button, two documents. In 3D it exports the view portfolio and
              says so, because a supplier receiving "the PDF" needs to know which
              of the two they got: the measurable layout, or the picture. */}
          <button
            className="tb-btn"
            title={
              view === '3d'
                ? 'Export the 3D views as a PDF portfolio — not to scale. Switch to 2D for the measurable layout sheet set.'
                : 'Export a scaled, vector PDF sheet set — choose paper, plot scale and issue status'
            }
            onClick={onExportPdf}
          >
            {view === '3d' ? '📄 3D PDF' : '📄 PDF'}
          </button>
          <button className="tb-btn" title="Save JSON (Ctrl+S)" onClick={() => engine.save()}>💾</button>
          <button className="tb-btn" title="Load JSON" onClick={() => loadRef.current?.click()}>📂</button>
          <input
            ref={loadRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) engine.loadFile(f);
              e.target.value = '';
            }}
          />
          <span className="tb-sep" />
          {/* 2D ↔ 3D. The 3D view reads the same document; it never edits it. */}
          <button
            className={'tb-btn' + (view === '3d' ? ' active' : '')}
            title={view === '3d' ? 'Back to the 2D drawing' : 'View this drawing as a 3D plant layout'}
            style={{ fontWeight: 700, letterSpacing: '0.02em' }}
            onClick={onToggleView}
          >
            {view === '3d' ? '▤ 2D' : '⬢ 3D'}
          </button>
          {/* The part modeller. A SEPARATE workspace with its own document —
              unlike 3D, this one is not a view of the drawing. */}
          <button
            className={'tb-btn' + (view === 'solid' ? ' active' : '')}
            title={
              view === 'solid'
                ? 'Back to the 2D drawing'
                : 'Model a part — hopper, feeder, chute, silo — from parametric solids'
            }
            style={{ fontWeight: 700, letterSpacing: '0.02em' }}
            onClick={onToggleSolid}
          >
            {view === 'solid' ? '▤ 2D' : '⬛ Solid'}
          </button>
          <span className="tb-sep" />
          <button className="tb-btn" title="Keyboard shortcuts" onClick={onHelp}>?</button>
          <button className="tb-btn" title="Clear canvas" style={{ color: '#ffb3b3' }} onClick={() => engine.clear()}>⌫</button>
        </div>

        {/* ── Secondary row: align / distribute / group ── */}
        <div id="toolbar2">
          <span className="tb-lbl">Align</span>
          <button className="tb-btn2" title="Align left" onClick={() => engine.align('left')}>◧</button>
          <button className="tb-btn2" title="Center horizontally" onClick={() => engine.align('cx')}>◫</button>
          <button className="tb-btn2" title="Align right" onClick={() => engine.align('right')}>◨</button>
          <button className="tb-btn2" title="Align top" onClick={() => engine.align('top')}>⬒</button>
          <button className="tb-btn2" title="Center vertically" onClick={() => engine.align('cy')}>⬌</button>
          <button className="tb-btn2" title="Align bottom" onClick={() => engine.align('bottom')}>⬓</button>
          <span className="tb-sep2" />
          <span className="tb-lbl">Distribute</span>
          <button className="tb-btn2" title="Distribute horizontally" onClick={() => engine.distribute('h')}>↔</button>
          <button className="tb-btn2" title="Distribute vertically" onClick={() => engine.distribute('v')}>↕</button>
          <span className="tb-sep2" />
          <button className="tb-btn2" title="Group (Ctrl+G)" onClick={() => engine.group()}>⊞ Grp</button>
          <button className="tb-btn2" title="Ungroup (Ctrl+Shift+G)" onClick={() => engine.ungroup()}>⊟ Ungrp</button>
        </div>
      </div>

      {/* ── Document name + meta ── */}
      <div id="docname-wrap">
        <input
          id="docname"
          placeholder="Untitled PFD"
          value={docName}
          onChange={(e) => {
            setDocName(e.target.value);
            engine.setDocName(e.target.value);
          }}
        />
        <div id="docmeta">
          Rev {engine.getDocMeta().rev} · {engine.getDocMeta().date}
          <span id="autosave-ind" className={savedAt ? 'saved' : ''}>
            {savedAt ? ` ● Saved ${savedAt}` : ' ●'}
          </span>
        </div>
      </div>
    </div>
  );
}
